
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import {
  calculateCurrentMonthACT,
  calculateCurrentYearMinusOnePSDO,
  calculatePreviousMonthACT,
  calculateTotal,
  calculateVariation,
  calculateVariation2
} from './utils/daily-sales-table-utils';

interface DailyItem {
  name: string;
  days: { [key: number]: number };
  previousMonthTotal: number;
  currentPastMonthTotal: number;
}

interface DealerData {
  name: string;
  models: string[];
  items: DailyItem[];
}

interface FlattenedRow {
  dealerName: string;
  item: DailyItem;
}

@Component
export default class DailySalesTable extends Vue {
  @Prop({ required: true }) readonly dealersData!: DealerData[];
  @Prop({ required: true }) readonly dayRange!: { start: number; end: number };
  @Prop({ required: true }) readonly currentMonth!: string;
  @Prop({ required: true }) readonly previousMonth!: string;
  @Prop({ required: true }) readonly year!: number;
  @Prop({ required: true }) readonly loading!: boolean;
  @Prop({ required: true }) hasAdmin!: boolean;

  editModes: boolean[] = [];

  get dynamicDays(): number[] {
    const days = [];
    for (let day = this.dayRange.start; day <= this.dayRange.end; day++) {
      days.push(day);
    }
    return days;
  }

  @Watch('dynamicDays')
  onDynamicDaysChange(newDays: number[]) {
    this.editModes = new Array(newDays.length).fill(false);
  }

  get processedData(): DealerData[] {
    return this.dealersData.map(dealer => ({
      ...dealer,
      items: (dealer.items && dealer.items.length > 0
        ? this.sortDealerItems(dealer.items)
        : this.initializeDealerItems(dealer))
    }));
  }

  private sortDealerItems(items: DailyItem[]): DailyItem[] {
    const priorityOrder = ['WALK IN', 'TEST DRIVE'];

    return items.sort((a, b) => {
      const aIndex = priorityOrder.indexOf(a.name);
      const bIndex = priorityOrder.indexOf(b.name);

      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      return a.name.localeCompare(b.name);
    });
  }

  get flattenedRows(): FlattenedRow[] {
    return this.processedData.reduce((acc: FlattenedRow[], dealer) => {
      dealer.items.forEach(item => {
        acc.push({ dealerName: dealer.name, item });
      });
      return acc;
    }, []);
  }

  resetEditModes() {
    this.editModes = new Array(this.dynamicDays.length).fill(false);
  }

  calculateTotal(days: { [key: number]: number }): number {
    return calculateTotal(days);
  }
  calculateVariation(item: DailyItem): string {
    return calculateVariation(item);
  }
  calculateVariation2(item: DailyItem): string {
    return calculateVariation2(item);
  }

  calculateCurrentMonthACT(dealerName: string): string {
    return calculateCurrentMonthACT(this.getDealerByName(dealerName));
  }
  calculatePreviousMonthACT(dealerName: string): string {
    return calculatePreviousMonthACT(this.getDealerByName(dealerName));
  }
  calculateCurrentYearMinusOnePSDO(dealerName: string): string {
    return calculateCurrentYearMinusOnePSDO(this.getDealerByName(dealerName));
  }

  getDealerByName(dealerName: string): DealerData {
    const dealer = this.dealersData.find(
      d => d.name.toUpperCase() === dealerName.toUpperCase()
    );
    return dealer ? dealer : { name: dealerName, models: [], items: [] };
  }

  isFirstRow(index: number): boolean {
    if (index === 0) return true;
    const previousRow = this.flattenedRows[index - 1];
    return previousRow && previousRow.dealerName !== this.flattenedRows[index].dealerName;
  }

  getDealerRowspan(dealerName: string): number {
    return this.flattenedRows.filter(
      row => row.dealerName.toUpperCase() === dealerName.toUpperCase()
    ).length;
  }

  toggleEdit(dayIndex: number) {
    console.log('[DailySalesTable] toggleEdit →', dayIndex);
    this.$set(this.editModes, dayIndex, !this.editModes[dayIndex]);
  }

  handleUpdate(dealer: string, item: string, day: number, value: number) {
    this.$emit('update-data', { dealer, item, day, value });
  }

  showDealerName(index: number, dealerName: string): string {
    if (index === 0) return dealerName;
    const previousRow = this.flattenedRows[index - 1];
    return previousRow && previousRow.dealerName === dealerName ? '' : dealerName;
  }

  private initializeDealerItems(dealer: DealerData): DailyItem[] {
    const baseItems = [
      {
        name: 'WALK IN',
        days: this.initializeDays(),
        previousMonthTotal: 0,
        currentPastMonthTotal: 0
      },
      {
        name: 'TEST DRIVE',
        days: this.initializeDays(),
        previousMonthTotal: 0,
        currentPastMonthTotal: 0
      }
    ];
    const modelItems = dealer.models.map(model => ({
      name: model,
      days: this.initializeDays(),
      previousMonthTotal: 0,
      currentPastMonthTotal: 0
    }));
    return [...baseItems, ...modelItems];
  }

  private initializeDays(): { [key: number]: number } {
    return this.dynamicDays.reduce((acc, day) => ({ ...acc, [day]: 0 }), {});
  }
}
